import React from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Instagram, Facebook, Twitter, Linkedin, Youtube } from "lucide-react";
import useSiteContact from "../hooks/useSiteContact";

const SOCIAL = [
  { key: "instagram_url", icon: Instagram, label: "Instagram" },
  { key: "facebook_url",  icon: Facebook,  label: "Facebook" },
  { key: "twitter_url",   icon: Twitter,   label: "Twitter" },
  { key: "linkedin_url",  icon: Linkedin,  label: "LinkedIn" },
  { key: "youtube_url",   icon: Youtube,   label: "YouTube" },
];

export default function Footer() {
  const c = useSiteContact();
  const socials = SOCIAL.filter((s) => c[s.key]);

  return (
    <footer data-testid="public-footer" className="bg-[#0A0A0A] text-white mt-24">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16 grid grid-cols-1 md:grid-cols-4 gap-10">
        <div className="md:col-span-2">
          <div className="font-display font-black text-2xl">
            Restro<span className="text-[var(--brand-accent)]">Setup</span>
          </div>
          <p className="mt-4 text-white/60 max-w-md leading-relaxed">
            We help hotels & restaurants attract more customers on Zomato & Swiggy through profile
            optimization, menu design, food photography, and brand identity.
          </p>
          {socials.length > 0 && (
            <div className="mt-6 flex gap-3">
              {socials.map(({ key, icon: Icon, label }) => (
                <a key={key} href={c[key]} target="_blank" rel="noreferrer"
                   aria-label={label} data-testid={`footer-social-${key}`}
                   className="w-9 h-9 rounded-sm bg-white/5 hover:bg-[var(--brand-primary)] flex items-center justify-center transition-colors">
                  <Icon size={16}/>
                </a>
              ))}
            </div>
          )}
        </div>

        <div>
          <div className="text-sm uppercase tracking-[0.2em] text-white/40 mb-4">Explore</div>
          <div className="flex flex-col gap-2 text-white/80">
            <Link to="/" className="hover:text-[var(--brand-primary)] transition-colors">Home</Link>
            <Link to="/services" className="hover:text-[var(--brand-primary)] transition-colors">Services</Link>
            <Link to="/portfolio" className="hover:text-[var(--brand-primary)] transition-colors">Portfolio</Link>
            <Link to="/contact" className="hover:text-[var(--brand-primary)] transition-colors">Contact</Link>
          </div>
        </div>

        <div>
          <div className="text-sm uppercase tracking-[0.2em] text-white/40 mb-4">Contact</div>
          <div className="flex flex-col gap-3 text-white/80">
            {(c.phones || []).map((p) => (
              <a key={p} href={`tel:${p}`}
                 data-testid={`footer-phone-${p.replace(/[^0-9]/g, '')}`}
                 className="flex items-center gap-2 hover:text-[var(--brand-primary)]">
                <Phone size={16}/> {p}
              </a>
            ))}
            {c.email && (
              <a href={`mailto:${c.email}`} data-testid="footer-email"
                 className="flex items-center gap-2 hover:text-[var(--brand-primary)]">
                <Mail size={16}/> {c.email}
              </a>
            )}
            {c.address && (
              <div className="flex items-start gap-2" data-testid="footer-address">
                <MapPin size={16} className="mt-0.5 shrink-0"/> <span className="leading-snug">{c.address}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-6 flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-white/40">
          <div>© {new Date().getFullYear()} RestroSetup. All rights reserved.</div>
          <Link to="/admin/login" className="hover:text-white/70" data-testid="footer-admin-link">Admin</Link>
        </div>
      </div>
    </footer>
  );
}
